import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Mic, RefreshCw, Download, Trash2, Star, Ear, Search, Clock, Sparkles, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  } catch { return iso; }
};
const fmtDur = (s) => {
  if (!s || s <= 0) return '-';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};
const fmtSize = (b) => {
  if (!b || b <= 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * VoicemailTab - admin inbox for voicemail submissions.
 *
 * Lists newest-first. Each row has an inline player, download link,
 * heard toggle, star toggle and delete. Filter chips for unheard / starred
 * and a search box across alias + message.
 */
const VoicemailTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | unheard | starred
  const [busy, setBusy] = useState(null);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/voicemail`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load voicemails');
      const data = await res.json();
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/admin/voicemail/${id}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error('Update failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete voicemail from ${row.alias || 'anonymous'}?\n\nThe audio file is also removed from storage.`)) return;
    setBusy(row.id);
    try {
      const r = await fetch(`${API}/api/admin/voicemail/${row.id}`, { method: 'DELETE', headers: auth });
      if (!r.ok) throw new Error('Delete failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const retranscribe = async (id) => {
    setBusy(id);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/voicemail/${id}/transcribe`, { method: 'POST', headers: auth });
      if (!r.ok) throw new Error('Retry failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    let list = rows;
    if (filter === 'unheard') list = list.filter((r) => !r.heard);
    if (filter === 'starred') list = list.filter((r) => r.starred);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((r) =>
        (r.alias || '').toLowerCase().includes(needle) ||
        (r.message || '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [rows, filter, q]);

  const unheardCount = rows.filter((r) => !r.heard).length;
  const starredCount = rows.filter((r) => r.starred).length;

  return (
    <div className="space-y-5" data-testid="voicemail-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Mic className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-black tracking-widest text-red-400 uppercase">Voicemails</h3>
            {unheardCount > 0 && (
              <span className="ml-1 text-[10px] font-black bg-red-600 text-white px-2 py-0.5 rounded-full">
                {unheardCount} new
              </span>
            )}
          </div>
          <p className="text-sm text-gray-400">
            Anonymous audio drops. Play them here, star the good ones, download to keep.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="voicemail-refresh"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="voicemail-filters">
        {[
          ['all', 'All', rows.length],
          ['unheard', 'Unheard', unheardCount],
          ['starred', 'Starred', starredCount],
        ].map(([k, label, count]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            data-testid={`voicemail-filter-${k}`}
            className={`px-3 py-1.5 rounded text-[11px] font-black uppercase tracking-widest border transition-colors ${
              filter === k
                ? 'bg-red-500/15 text-red-300 border-red-500/60'
                : 'bg-zinc-900 text-zinc-400 border-zinc-700 hover:text-white'
            }`}
          >
            {label} <span className="text-zinc-500">{count}</span>
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search alias or note…"
          data-testid="voicemail-search"
          className="w-full sm:max-w-sm bg-black/50 border border-red-500/25 text-white pl-9 pr-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
        />
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm" data-testid="voicemail-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="voicemail-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400" data-testid="voicemail-empty">
          {rows.length === 0 ? 'No voicemails yet.' : 'No voicemails match this filter.'}
        </div>
      ) : (
        <div className="space-y-3" data-testid="voicemail-list">
          {filtered.map((v) => {
            const isBusy = busy === v.id;
            return (
              <div
                key={v.id}
                data-testid={`voicemail-row-${v.id}`}
                className={`bg-zinc-900/80 border rounded-lg p-4 space-y-3 ${
                  v.heard ? 'border-zinc-800' : 'border-red-500/40 shadow-[0_0_18px_rgba(220,38,38,0.15)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-black text-base flex items-center gap-2">
                      {v.alias || <span className="italic text-zinc-500">Anonymous</span>}
                      {!v.heard && <span className="text-[9px] font-black bg-red-600 text-white px-1.5 py-0.5 rounded uppercase tracking-widest">New</span>}
                      {v.starred && <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />}
                    </p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <Clock className="h-3 w-3" /> {fmtDate(v.created_at)}
                      <span>·</span>
                      <span>Duration {fmtDur(v.duration_sec)}</span>
                      {v.size_bytes ? (<><span>·</span><span>{fmtSize(v.size_bytes)}</span></>) : null}
                    </p>
                  </div>
                </div>

                <audio
                  src={v.public_url}
                  controls
                  preload="none"
                  className="w-full"
                  data-testid={`voicemail-audio-${v.id}`}
                  onPlay={() => { if (!v.heard) patch(v.id, { heard: true }); }}
                />

                {v.message && (
                  <p className="text-sm text-zinc-200 bg-black/40 border border-red-500/15 rounded px-3 py-2 whitespace-pre-wrap">
                    {v.message}
                  </p>
                )}

                {/* Whisper transcript block */}
                <div data-testid={`voicemail-transcript-block-${v.id}`}>
                  {v.transcription_status === 'queued' || v.transcription_status === 'running' ? (
                    <div className="flex items-center gap-2 text-[11px] text-zinc-400 bg-black/40 border border-zinc-800 rounded px-3 py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-400" />
                      <span className="uppercase tracking-widest font-black">Transcribing…</span>
                    </div>
                  ) : v.transcription_status === 'error' ? (
                    <div className="flex items-center justify-between gap-2 text-[11px] bg-black/40 border border-red-500/30 rounded px-3 py-2 flex-wrap">
                      <span className="text-red-300">Transcription failed{v.transcription_error ? `: ${v.transcription_error}` : '.'}</span>
                      <button
                        type="button"
                        onClick={() => retranscribe(v.id)}
                        disabled={isBusy}
                        data-testid={`voicemail-retranscribe-${v.id}`}
                        className="inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded"
                      >
                        <Sparkles className="h-3 w-3" /> Retry
                      </button>
                    </div>
                  ) : v.transcript ? (
                    <div className="bg-emerald-500/5 border border-emerald-500/25 rounded px-3 py-2 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-400 flex items-center gap-1.5">
                          <Sparkles className="h-3 w-3" /> Whisper transcript
                        </p>
                        <button
                          type="button"
                          onClick={() => retranscribe(v.id)}
                          disabled={isBusy}
                          data-testid={`voicemail-retranscribe-${v.id}`}
                          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white font-black"
                          title="Re-run Whisper"
                        >
                          Re-run
                        </button>
                      </div>
                      <p className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed" data-testid={`voicemail-transcript-${v.id}`}>
                        {v.transcript}
                      </p>
                    </div>
                  ) : v.transcription_status === 'empty' ? (
                    <p className="text-[11px] text-zinc-500 italic px-3">No speech detected in this recording.</p>
                  ) : null}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <a
                    href={v.public_url}
                    download
                    data-testid={`voicemail-download-${v.id}`}
                    className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                  <button
                    type="button"
                    onClick={() => patch(v.id, { heard: !v.heard })}
                    disabled={isBusy}
                    data-testid={`voicemail-heard-${v.id}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded ${
                      v.heard
                        ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                        : 'bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-100'
                    }`}
                  >
                    <Ear className="h-3.5 w-3.5" />
                    {v.heard ? 'Mark unheard' : 'Mark heard'}
                  </button>
                  <button
                    type="button"
                    onClick={() => patch(v.id, { starred: !v.starred })}
                    disabled={isBusy}
                    data-testid={`voicemail-star-${v.id}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded ${
                      v.starred
                        ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
                    }`}
                  >
                    <Star className={`h-3.5 w-3.5 ${v.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                    {v.starred ? 'Starred' : 'Star'}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(v)}
                    disabled={isBusy}
                    data-testid={`voicemail-delete-${v.id}`}
                    className="inline-flex items-center gap-1.5 bg-red-800/40 hover:bg-red-700/60 text-red-100 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded ml-auto"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VoicemailTab;
